// includes/js-src/trap-disarm/rune.ts — RUNE TRACING, the magical trap-disarm board.
//
// Promoted from the demo bench 2026-08-05 (owner: "the prototype is much better let's promote
// this to the real thing", then "for magical we are going with the tracing one given the current
// system"). It REPLACES Glyph Unweaving (magical.ts), which stays in the bundle only because a
// token minted before the switch carries weave geometry for a full 30-minute TTL.
//
// THE GAME. Drag the athame along the carved strokes of a stone glyph. Cover every stroke to
// complete a rune; complete every rune to disarm. The Void tightens while the blade is DOWN and
// FREEZES the instant you lift it, so reading the board is free and tracing is what costs.
// Straying outside the corridor costs much more, and wards hidden along the strokes cost most of
// all — unless you ring one with the blade to bind it shut.
//
// ⭐ WHY THIS IS NOT A DEXTERITY TEST. A tracing game's obvious failure is that it measures the
// PLAYER's pointer precision rather than the CHARACTER's skill — a drawing tablet would beat a
// trackpad regardless of build, which inverts what the engine is for. Three server-minted ladders
// keep the character dominant, none of them "steadiness":
//   corridor    — margin around the true path            (skill buys TOLERANCE)
//   guidance    — how hard the blade is pulled onto it   (skill buys ASSISTANCE)
//   ward_sense  — how far off a ward is felt, how clearly (skill buys INFORMATION)
// ward_sense is the important one: it turns the skill axis back into knowing-what-is-there,
// exactly as feel_radius does on the mechanical board.
//
// ⚠ EVERY DIFFICULTY NUMBER IS SERVER-MINTED. Nothing in this file may invent one. The
// authority is includes/functions/inventory/trap_disarm_minigame_inc.php.

import type { RuneDefinition, RuneGeometry, RuneStroke, RuneWard, TrapDisarmShell } from './trap-disarm';
import { TENSION_AMBER, TENSION_RED, TensionSim, resolveTensionConfig } from './mechanical';

/** Coverage bins per stroke. A stroke counts as traced at 94% of them. */
const BINS = 26;
/** How far round a ward the blade must travel to bind it, in half-turns (1.6π ≈ 0.8 of a lap). */
const SEAL_TURNS = 1.6;
/** Fallback for geometry minted before seal_outer shipped. Matches TRAP_MINIGAME_SEAL_OUTER. */
const SEAL_OUTER_DEFAULT = 3.6;

interface LiveWard extends RuneWard {
    seal?: number;
    sealing?: boolean;
    lastAng?: number;
    taught?: boolean;
    felt?: boolean;
}

// ⚠ The whole module indexes arrays through guarded locals (`const a = pts[i]; if (!a) continue;`)
// rather than trusting the subscript. tsconfig.base.json runs noUncheckedIndexedAccess, so every
// arr[i] is T | undefined; this is the same idiom mechanical.ts uses, and it keeps the trap-disarm
// module at zero diagnostics in a repo that otherwise carries hundreds.

/** Total length of a polyline in normalized units. */
function polyLength(pts: Array<{ x: number; y: number }>): number {
    let len = 0;
    for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1];
        const b = pts[i];
        if (!a || !b) continue;
        len += Math.hypot(b.x - a.x, b.y - a.y);
    }
    return len;
}

/**
 * The point a fraction t along a stroke, by ARC LENGTH. A bowed segment carries eight vertices
 * and a straight one carries a single vertex, so index interpolation would bunch every ward onto
 * the curves. Mirrors ft_trap_minigame_point_along().
 */
function pointAlong(stroke: RuneStroke, t: number): { x: number; y: number } {
    const pts = stroke.pts;
    const last = pts[pts.length - 1] ?? { x: 0.5, y: 0.5 };
    const target = stroke.len * Math.max(0, Math.min(1, t));
    let acc = 0;
    for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1];
        const b = pts[i];
        if (!a || !b) continue;
        const seg = Math.hypot(b.x - a.x, b.y - a.y);
        if (acc + seg >= target) {
            const f = seg > 0 ? (target - acc) / seg : 0;
            return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
        }
        acc += seg;
    }
    return last;
}

/**
 * A rune's stroke by index, guarded. Ward.stroke and activeStroke are server-minted or derived
 * from a search over the same array, so they are in range in practice — but a malformed payload
 * should degrade to "skip this hazard" rather than throw inside an animation frame, which would
 * kill the board mid-attempt and strand the player's AP.
 */
function strokeAt(r: RuneDefinition, i: number): RuneStroke | null {
    return r.strokes[i] ?? null;
}

/** The normalized centre of a ward, or null if its stroke index does not resolve. */
function wardPoint(r: RuneDefinition, w: RuneWard): { x: number; y: number } | null {
    const st = strokeAt(r, w.stroke);
    return st ? pointAlong(st, w.t) : null;
}

/** Nearest point on a polyline, carrying the segment index so corridor and coverage share a walk. */
function nearestOnStroke(stroke: RuneStroke, p: { x: number; y: number }) {
    let best = { d: Infinity, x: 0, y: 0, seg: 0, f: 0 };
    for (let i = 1; i < stroke.pts.length; i++) {
        const a = stroke.pts[i - 1];
        const b = stroke.pts[i];
        if (!a || !b) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const l2 = dx * dx + dy * dy;
        const f = l2 > 1e-12 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0;
        const qx = a.x + dx * f;
        const qy = a.y + dy * f;
        const d = Math.hypot(p.x - qx, p.y - qy);
        if (d < best.d) best = { d, x: qx, y: qy, seg: i - 1, f };
    }
    return best;
}

export function mountRuneGame(stage: HTMLElement, shell: TrapDisarmShell): void {
    const geo = shell.cfg.geometry as RuneGeometry;
    const model = geo.tension;
    // A board with no runes is not playable and must not silently "complete" — the bench's original
    // rune submit fell through to the mechanical validator and reported DISARMED for an empty
    // board. Bail to a safe fail instead.
    if (!Array.isArray(geo.runes) || geo.runes.length === 0 || !model) { shell.cancel(); return; }
    const SEAL_OUTER = typeof geo.seal_outer === 'number' && geo.seal_outer > 1
        ? geo.seal_outer
        : SEAL_OUTER_DEFAULT;

    // ⚠ The ft-trap-disarm-canvas class is load-bearing, not decoration: it is how the panel's CSS
    // and the staging E2E find the board. The bench prototype this was ported from is a standalone
    // page and never needed it, so it was absent from the source material.
    stage.innerHTML = '<canvas class="ft-trap-disarm-canvas" style="width:100%;touch-action:none;display:block;"></canvas>';
    const canvas = stage.querySelector('canvas') as HTMLCanvasElement | null;
    if (!canvas) { shell.cancel(); return; }
    const cssW = Math.max(260, Math.min(420, stage.clientWidth || 320));
    const cssH = Math.min(330, cssW + 20);
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.height = cssH + 'px';
    const ctx = canvas.getContext('2d');
    if (!ctx) { shell.cancel(); return; }
    ctx.scale(dpr, dpr);

    shell.setHint('Trace the rune with the athame. The Void tightens while the blade is DOWN and '
        + 'rests the instant you lift it — lift freely to read your route. Stay inside the line. You '
        + 'only feel a ward through the blade, so they surface as you trace: swing wide around one, '
        + 'or ring it to bind it shut.');

    // Board space: a square play area with the tension gauge down the right.
    const gaugeW = 22;
    const PAD = 10;
    const boardW = cssW - gaugeW - PAD * 3;
    const boardH = cssH - PAD * 2 - 16;
    const side = Math.min(boardW, boardH);
    const oxs = PAD + (boardW - side) / 2;
    const oys = PAD + (boardH - side) / 2;
    const toPx = (p: { x: number; y: number }) => ({ x: oxs + p.x * side, y: oys + p.y * side });
    const toRune = (x: number, y: number) => ({ x: (x - oxs) / side, y: (y - oys) / side });

    // ── E2E hook (read-only) ─────────────────────────────────────────────────────────────────
    // tools/test/codex-trap-disarm-magical-e2e-staging-smoke.mjs drives a real board on staging and
    // has to put a synthetic pointer exactly on a stroke. It publishes the LAYOUT rather than
    // letting the test recompute it: the weave smoke keeps its own copy of the client's circle
    // maths, and three stale copies of the mechanical board's lane maths once failed a board that
    // played perfectly by hand. A test that invents its own geometry stops testing the real one.
    const hookHost = window as unknown as Record<string, unknown>;
    let runeIdx = 0;
    let strokeDone: boolean[] = [];
    let covered: boolean[][] = [];
    const resetRune = () => {
        const r = geo.runes[runeIdx];
        if (!r) return;
        strokeDone = r.strokes.map(() => false);
        covered = r.strokes.map(() => new Array(BINS).fill(false) as boolean[]);
        // Ward run-state is per-attempt and lives on the ward object; a re-mounted board must not
        // inherit a half-closed seal ring from the previous rune.
        for (const w of r.wards as LiveWard[]) {
            w.sealing = false;
            w.seal = 0;
            w.felt = false;
        }
    };
    resetRune();

    // The rune board drives floor/live directly rather than through move()/step()/endDrag(), which
    // are shaped for sears on tracks. It is the same needle and the same PERFECT line.
    const sim = new TensionSim(resolveTensionConfig({}));
    let down = false;
    let activeStroke = -1;
    let bladePointerId = -1;
    let blade = { x: 0.5, y: 0.5 };
    let intent = { x: 0.5, y: 0.5 };
    let prevBlade = { x: 0.5, y: 0.5 };
    let tension = 0;
    let shownTension = 0;
    let strainT = 0;
    let finished = false;
    let lastTs = 0;
    let offNow = false;
    let sealingWard: LiveWard | null = null;
    let lastDragCue = 0;
    let lastChargeCue = 0;

    const now = () => (window.performance || Date).now();
    // runeIdx runs one PAST the last rune the moment the board completes, so every reader clamps.
    // The non-null assert is earned by the emptiness guard at the top of the mount.
    const liveRune = (): RuneDefinition => geo.runes[Math.min(runeIdx, geo.runes.length - 1)] as RuneDefinition;

    const pointerPos = (ev: PointerEvent) => {
        const r = canvas.getBoundingClientRect();
        return toRune((ev.clientX - r.left) * (cssW / (r.width || cssW)), (ev.clientY - r.top) * (cssH / (r.height || cssH)));
    };

    hookHost.__ftTrapRune = {
        /** Normalized board point → CSS px within the canvas. The client's own mapping. */
        toPx: (p: { x: number; y: number }) => toPx(p),
        /** Which rune is live, which strokes are still owed, and whether the blade is down. */
        state: () => ({
            runeIdx,
            total: geo.runes.length,
            strokes: liveRune().strokes,
            strokeDone: strokeDone.slice(),
            down,
            tension,
            peak: sim.max,
            finished,
        }),
    };
    const clearHook = () => { try { delete hookHost.__ftTrapRune; } catch { hookHost.__ftTrapRune = undefined; } };

    /**
     * GUIDANCE. The pointer is intent; the blade is drawn toward the nearest unfinished stroke.
     * Strength and capture scale with skill edge — this is what lets a skilled character beat a
     * bad mouse.
     *
     * ⛔ GUIDANCE STOPS AT A WARD'S WORKING BAND. Sealing means deliberately leaving the line, and
     * an assist that drags the blade back onto the path does not merely fail to help — it PREVENTS
     * the counterplay, flattening your circle into a wobble along the stroke so the ring never
     * closes. Near a ward you take the blade in your own hand.
     */
    const applyGuidance = (p: { x: number; y: number }) => {
        const r = liveRune();
        for (const w of r.wards) {
            if (w.state !== 'armed') continue;
            const wp = wardPoint(r, w);
            if (!wp) continue;
            if (Math.hypot(p.x - wp.x, p.y - wp.y) <= w.r * SEAL_OUTER) {
                return { p, stroke: activeStroke, dist: Infinity };
            }
        }
        let best: ReturnType<typeof nearestOnStroke> | null = null;
        let bestIdx = -1;
        for (let s = 0; s < r.strokes.length; s++) {
            if (strokeDone[s]) continue;
            const st = strokeAt(r, s);
            if (!st) continue;
            const n = nearestOnStroke(st, p);
            if (!best || n.d < best.d) { best = n; bestIdx = s; }
        }
        if (!best) return { p, stroke: -1, dist: Infinity };
        const g = geo.guidance;
        if (best.d < g.capture) {
            const k = g.pull * (1 - best.d / g.capture);
            return {
                p: { x: p.x + (best.x - p.x) * k, y: p.y + (best.y - p.y) * k },
                stroke: bestIdx,
                dist: best.d * (1 - k),
            };
        }
        return { p, stroke: bestIdx, dist: best.d };
    };

    const beginStroke = () => {
        const r = liveRune();
        const g = applyGuidance(intent);
        blade = g.p;
        if (g.stroke < 0) return;
        // Order binds on the nastier traps: starting the wrong stroke is a MISREAD, not a free retry.
        if (r.orderMatters) {
            let expected = -1;
            for (let s = 0; s < r.strokes.length; s++) if (!strokeDone[s]) { expected = s; break; }
            if (expected >= 0 && g.stroke !== expected) {
                sim.floor = Math.min(100, sim.floor + model.misread_floor_ratchet);
                shell.setHint('That is not the stroke the Void expects next. The glyph resists.');
                shell.sfx.play('surge', 0.5, 1.05);
                return;
            }
        }
        activeStroke = g.stroke;
    };

    canvas.addEventListener('pointerdown', (ev) => {
        if (finished || down) return;
        intent = pointerPos(ev);
        down = true;
        bladePointerId = ev.pointerId;
        try { canvas.setPointerCapture(ev.pointerId); } catch { /* not fatal */ }
        beginStroke();
        // The sweep starts HERE, not from wherever the blade was left last time — otherwise setting
        // the blade down would sweep a line across the board and mark every bin it crossed.
        prevBlade = { x: blade.x, y: blade.y };
    });

    canvas.addEventListener('pointermove', (ev) => {
        if (finished) return;
        if (down && ev.pointerId !== bladePointerId) return;
        intent = pointerPos(ev);
        if (!down) { blade = applyGuidance(intent).p; return; }
        blade = applyGuidance(intent).p;
        // ⚠ Do NOT adopt a stroke here. Assigning activeStroke on the first move silently undid a
        // MISREAD: you pressed on the wrong stroke, were told so, then traced it anyway.
        //
        // The drag layer is TWO DISTINCT CUES, not one at two volumes: 'scratch' is the blade
        // tracking inside the carved channel, 'stray' is it grinding across raw stone. Being off
        // the line is the board's core feedback, so it must be audible as a different SOUND within
        // a frame — a louder copy of the same file reads as "closer", not as "wrong".
        if (now() - lastDragCue > 220) {
            lastDragCue = now();
            if (offNow) shell.sfx.play('stray', 0.34, 0.92 + Math.random() * 0.12);
            else shell.sfx.play('scratch', 0.20, 0.94 + Math.random() * 0.12);
        }
    });

    // Lifting is FREE and freezes the tension. Nothing is charged here.
    const lift = (ev: PointerEvent | null) => {
        if (!down) return;
        if (ev && ev.pointerId !== undefined && ev.pointerId !== bladePointerId) return;
        down = false;
        activeStroke = -1;
        sealingWard = null;
    };
    canvas.addEventListener('pointerup', lift);
    canvas.addEventListener('pointercancel', lift);
    canvas.addEventListener('lostpointercapture', lift);
    window.addEventListener('blur', () => lift(null));

    const frame = (ts: number) => {
        if (finished) return;
        if (!shell.stillMounted()) { finished = true; clearHook(); shell.cancel(true); return; }
        const dt = lastTs > 0 ? Math.min(0.1, (ts - lastTs) / 1000) : 0.016;
        lastTs = ts;

        const r = liveRune();
        offNow = false;

        if (down) {
            // ── blade down: the Void tightens ────────────────────────────────────────────────
            sim.live += model.down_live_per_second * dt;

            // In the working band of an armed ward? Sealing REQUIRES leaving the line, so charging
            // the off-corridor rate on top of the seal rate would punish the counterplay twice and
            // read as broken — you would watch the needle leap while doing the correct thing.
            let sealingBand: LiveWard | null = null;
            for (const w of r.wards as LiveWard[]) {
                if (w.state !== 'armed') continue;
                const wp = wardPoint(r, w);
                if (!wp) continue;
                const d = Math.hypot(blade.x - wp.x, blade.y - wp.y);
                if (d > w.r && d <= w.r * SEAL_OUTER) { sealingBand = w; break; }
            }

            const active = activeStroke >= 0 ? strokeAt(r, activeStroke) : null;
            const near = active ? nearestOnStroke(active, blade) : null;
            if (near && near.d > geo.corridor && !sealingBand) {
                offNow = true;
                sim.live += model.off_live_per_second * dt;
            }

            // ⚠ COVERAGE AND WARDS ARE SWEPT, never sampled at the blade's current point. A pointer
            // reports at whatever rate the device feels like — a 1000Hz mouse lands a dozen samples
            // where a 60Hz digitizer lands one — so testing only the instantaneous position would
            // let a fast stroke tunnel through coverage bins AND through wards, and would make the
            // game measurably easier on better hardware. Walking the segment at a fraction of the
            // corridor width makes the result depend on the PATH, which is the same everywhere.
            //
            // Wards answer to the BLADE, not to whether a stroke is legitimately under way —
            // dragging a live athame across a ward sets it off whatever you meant to be doing.
            // Coverage is the part that requires a properly started stroke.
            const st = active;
            const bins = activeStroke >= 0 ? covered[activeStroke] ?? null : null;
            const span = Math.hypot(blade.x - prevBlade.x, blade.y - prevBlade.y);
            const steps = Math.max(1, Math.min(96, Math.ceil(span / (geo.corridor * 0.35))));
            for (let k = 1; k <= steps; k++) {
                const q = {
                    x: prevBlade.x + (blade.x - prevBlade.x) * (k / steps),
                    y: prevBlade.y + (blade.y - prevBlade.y) * (k / steps),
                };
                if (st && bins) {
                    const nq = nearestOnStroke(st, q);
                    const segA = st.pts[nq.seg];
                    const segB = st.pts[nq.seg + 1];
                    if (nq.d <= geo.corridor && segA && segB) {
                        let acc = 0;
                        for (let i = 1; i <= nq.seg; i++) {
                            const a = st.pts[i - 1];
                            const b = st.pts[i];
                            if (!a || !b) continue;
                            acc += Math.hypot(b.x - a.x, b.y - a.y);
                        }
                        const segLen = Math.hypot(segB.x - segA.x, segB.y - segA.y);
                        const t = (acc + segLen * nq.f) / Math.max(1e-6, st.len);
                        bins[Math.max(0, Math.min(BINS - 1, Math.floor(t * BINS)))] = true;
                    }
                }
                for (const w of r.wards as LiveWard[]) {
                    if (w.state !== 'armed') continue;
                    const wp = wardPoint(r, w);
                    if (!wp) continue;
                    if (Math.hypot(q.x - wp.x, q.y - wp.y) <= w.r) {
                        w.state = 'triggered';
                        sim.live += model.ward_live_pulse;
                        sim.floor = Math.min(100, sim.floor + model.ward_floor_ratchet);
                        shell.sfx.play('discharge', 0.7, 1.0);
                        shell.setHint('A ward flares under the blade — the Void surges.');
                    }
                }
            }

            // ── ward sealing (proximity; the swept pass above owns the hits) ──────────────────
            for (const w of r.wards as LiveWard[]) {
                if (w.state !== 'armed') continue;
                const wp = wardPoint(r, w);
                if (!wp) continue;
                const d = Math.hypot(blade.x - wp.x, blade.y - wp.y);

                // A ward SURFACING under the blade. Wards are blade-only, which removed the board's
                // whole warning channel from the eyes — the player is watching the line they are
                // tracing, not the stone ahead of it. Sound the discovery once, the first time the
                // character feels it; re-announcing on every re-approach turns it into wallpaper.
                if (!w.felt && d <= geo.ward_sense.radius) {
                    w.felt = true;
                    // Clarity is the skill ladder: a character who can barely feel the ward gets a
                    // fainter, less certain sound, as they get a fainter, wobblier ring drawn.
                    shell.sfx.play('ward', 0.35 + geo.ward_sense.clarity * 0.35, 1.0);
                }

                if (d > w.r && d <= w.r * SEAL_OUTER) {
                    // SEALING: ring the ward and it is bound shut. The band is wide (just outside
                    // the kill radius, out to SEAL_OUTER) because a thin annulus is a dexterity
                    // test in a game trying not to be one — and drifting inward while circling
                    // would set off the very ward you are binding.
                    const ang = Math.atan2(blade.y - wp.y, blade.x - wp.x);
                    // ⚠ Seal progress lives ON THE WARD, not in one global slot. It used to be
                    // global, so passing through a second ward's band silently reset the ring you
                    // were part-way through — which reads as the mechanic simply not working.
                    if (!w.sealing) { w.sealing = true; w.seal = 0; w.lastAng = ang; }
                    sealingWard = w;
                    let dA = ang - (w.lastAng ?? ang);
                    while (dA > Math.PI) dA -= Math.PI * 2;
                    while (dA < -Math.PI) dA += Math.PI * 2;
                    w.seal = (w.seal ?? 0) + dA;
                    w.lastAng = ang;
                    sim.live += model.seal_live_per_second * dt;
                    if (!w.taught) {
                        w.taught = true;
                        shell.setHint('Keep circling — ring the ward all the way round and it binds shut.');
                    }
                    if (Math.abs(w.seal ?? 0) >= Math.PI * SEAL_TURNS) {
                        w.state = 'sealed';
                        w.sealing = false;
                        if (sealingWard === w) sealingWard = null;
                        shell.sfx.play('seal', 0.7, 1.0);
                        shell.setHint('The ward is bound shut. That stretch of the line is safe now.');
                    }
                } else if (w.sealing && d > w.r * SEAL_OUTER) {
                    // Wandered out of this ward's band — the ring lapses, and costs nothing.
                    w.sealing = false;
                    w.seal = 0;
                    if (sealingWard === w) sealingWard = null;
                }
            }
        }
        // Blade UP: FROZEN. No accrual, and no decay — decay would make the optimal play tapping
        // along the path in tiny bursts, which is not a game.
        prevBlade = { x: blade.x, y: blade.y };

        if (sim.value > sim.max) sim.max = sim.value;
        tension = sim.value;
        shownTension = tension > shownTension
            ? tension
            : shownTension + (tension - shownTension) * Math.min(1, dt * 7);

        // ── stroke / rune completion ─────────────────────────────────────────────────────────
        for (let s = 0; s < r.strokes.length; s++) {
            if (strokeDone[s]) continue;
            const bins = covered[s];
            if (!bins) continue;
            let got = 0;
            for (let b = 0; b < BINS; b++) if (bins[b]) got++;
            if (got >= Math.ceil(BINS * 0.94)) {
                strokeDone[s] = true;
                if (activeStroke === s) activeStroke = -1;
                shell.sfx.play('ignite', 0.6, 1.0);
            }
        }
        if (strokeDone.every((x) => x)) {
            // A completed rune vents, exactly as seating a sear does on the mechanical board.
            sim.floor = Math.max(0, sim.floor - model.rune_floor_relief);
            sim.live *= model.live_retention_on_rune;
            shell.sfx.play('bind', 0.85, 1.0);
            runeIdx++;
            if (runeIdx >= geo.runes.length) {
                finished = true;
                clearHook();
                shell.submit({
                    runes_completed: geo.runes.length,
                    never_left_green: sim.max < TENSION_AMBER,
                });
                return;
            }
            resetRune();
            down = false;
            activeStroke = -1;
            shell.setHint('The rune yields. Another binding waits beneath it.');
        }

        if (tension >= TENSION_RED) {
            strainT += dt;
            const sf = Math.min(1, strainT / geo.strain_seconds);
            if (ts - lastChargeCue > (450 - sf * 300)) {
                shell.sfx.play('charge', 0.6 + sf * 0.3, 0.9 + sf * 0.45);
                lastChargeCue = ts;
            }
            if (strainT >= geo.strain_seconds) { finished = true; clearHook(); shell.spring(); return; }
        } else {
            strainT = 0;
        }

        draw(ts);
        requestAnimationFrame(frame);
    };

    const draw = (ts: number) => {
        const r = liveRune();
        ctx.clearRect(0, 0, cssW, cssH);
        ctx.fillStyle = '#12121a';
        ctx.fillRect(0, 0, cssW, cssH);

        // Corridor, then centreline, then the lit portion already traced.
        for (let s = 0; s < r.strokes.length; s++) {
            const st = strokeAt(r, s);
            const bins = covered[s];
            if (!st) continue;
            const isDone = strokeDone[s];
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.strokeStyle = 'rgba(120,140,220,.13)';
            ctx.lineWidth = geo.corridor * side * 2;
            ctx.beginPath();
            for (let i = 0; i < st.pts.length; i++) {
                const raw = st.pts[i];
                if (!raw) continue;
                const p = toPx(raw);
                if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
            }
            ctx.stroke();
            ctx.strokeStyle = isDone ? 'rgba(130,240,180,.95)' : 'rgba(150,175,255,.55)';
            ctx.lineWidth = isDone ? 3 : 2;
            ctx.stroke();

            if (!isDone && bins) {
                ctx.strokeStyle = 'rgba(150,240,255,.9)';
                ctx.lineWidth = 3;
                for (let b = 0; b < BINS; b++) {
                    if (!bins[b]) continue;
                    const a = toPx(pointAlong(st, b / BINS));
                    const c = toPx(pointAlong(st, (b + 1) / BINS));
                    ctx.beginPath();
                    ctx.moveTo(a.x, a.y);
                    ctx.lineTo(c.x, c.y);
                    ctx.stroke();
                }
            }
        }

        // The next stroke, when order matters — announced as loudly as the character can read it.
        if (r.orderMatters) {
            let expected = -1;
            for (let s = 0; s < r.strokes.length; s++) if (!strokeDone[s]) { expected = s; break; }
            const expectedStroke = expected >= 0 ? strokeAt(r, expected) : null;
            const headPt = expectedStroke?.pts[0];
            if (headPt) {
                const clarity = geo.ward_sense.clarity;
                const head = toPx(headPt);
                ctx.fillStyle = 'rgba(255,235,150,' + (0.15 + clarity * 0.7).toFixed(2) + ')';
                ctx.beginPath();
                ctx.arc(head.x, head.y, 5 + Math.sin(ts / 260) * 1.5, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        // ── wards, rendered by how well the CHARACTER senses them ────────────────────────────
        const sense = geo.ward_sense;
        for (let w = 0; w < r.wards.length; w++) {
            const ward = r.wards[w] as LiveWard | undefined;
            if (!ward) continue;
            const wpN = wardPoint(r, ward);
            if (!wpN) continue;
            const wp = toPx(wpN);
            const dist = Math.hypot(blade.x - wpN.x, blade.y - wpN.y);

            if (ward.state === 'sealed') {
                ctx.strokeStyle = 'rgba(130,240,180,.85)';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.arc(wp.x, wp.y, ward.r * side, 0, Math.PI * 2);
                ctx.stroke();
                continue;
            }
            if (ward.state === 'triggered') {
                ctx.fillStyle = 'rgba(255,90,90,.30)';
                ctx.beginPath();
                ctx.arc(wp.x, wp.y, ward.r * side, 0, Math.PI * 2);
                ctx.fill();
                continue;
            }
            // ⛔ ARMED WARDS ARE ONLY FELT THROUGH THE BLADE — nothing is revealed while it is up.
            // Reveal used to follow the mouse on HOVER, which was free reconnaissance: sweep the
            // pointer over the rune, map every ward at zero tension, then start tracing. Worse, it
            // was reconnaissance DESKTOP ONLY — a touch device has no hover, so the two platforms
            // were playing different games (owner, 2026-08-04). Sensing a ward now costs exactly
            // what everything else costs: blade down, tension climbing.
            if (!down) continue;
            if (dist > sense.radius) continue;

            const prox = 1 - dist / sense.radius;
            const a = (0.12 + prox * 0.55) * (0.35 + sense.clarity * 0.65);
            const wob = (1 - sense.clarity) * 4;
            const jx = Math.sin(ts / 190 + w) * wob;
            const jy = Math.cos(ts / 230 + w) * wob;
            const rr = ward.r * side * (0.6 + sense.clarity * 0.4);
            const gg = ctx.createRadialGradient(wp.x + jx, wp.y + jy, 1, wp.x + jx, wp.y + jy, rr * 2.2);
            gg.addColorStop(0, 'rgba(255,120,120,' + a.toFixed(3) + ')');
            gg.addColorStop(1, 'rgba(255,120,120,0)');
            ctx.fillStyle = gg;
            ctx.beginPath();
            ctx.arc(wp.x + jx, wp.y + jy, rr * 2.2, 0, Math.PI * 2);
            ctx.fill();
            if (sense.clarity > 0.5) {
                ctx.strokeStyle = 'rgba(255,140,140,' + (sense.clarity * 0.8).toFixed(2) + ')';
                ctx.lineWidth = 1.2;
                ctx.setLineDash([3, 3]);
                ctx.beginPath();
                ctx.arc(wp.x, wp.y, ward.r * side, 0, Math.PI * 2);
                ctx.stroke();
                ctx.setLineDash([]);
            }

            // ── THE COUNTERPLAY, MADE VISIBLE ────────────────────────────────────────────────
            // A mechanic nobody can find is a mechanic that does not exist: the owner sensed wards
            // and had no idea what to do about them (2026-08-04). Once a ward is close enough to
            // work on, the seal band is DRAWN — a dotted ring saying "carry the blade round here" —
            // and the arc fills as you circle it. The kill radius is drawn as a solid core inside
            // it so the two are never confused.
            const workable = down && (dist < sense.radius * 0.75 || sealingWard === ward);
            if (workable) {
                const ringR = ward.r * side * ((1 + SEAL_OUTER) / 2);
                ctx.strokeStyle = 'rgba(255,220,140,.30)';
                ctx.lineWidth = 1;
                ctx.setLineDash([2, 4]);
                ctx.beginPath();
                ctx.arc(wp.x, wp.y, ringR, 0, Math.PI * 2);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.fillStyle = 'rgba(255,120,120,.20)';
                ctx.beginPath();
                ctx.arc(wp.x, wp.y, ward.r * side, 0, Math.PI * 2);
                ctx.fill();
                if (sealingWard === ward) {
                    ctx.strokeStyle = 'rgba(255,220,140,.95)';
                    ctx.lineWidth = 3;
                    ctx.beginPath();
                    ctx.arc(wp.x, wp.y, ringR, -Math.PI / 2,
                        -Math.PI / 2 + Math.min(1, Math.abs(ward.seal ?? 0) / (Math.PI * SEAL_TURNS)) * Math.PI * 2);
                    ctx.stroke();
                } else {
                    ctx.fillStyle = 'rgba(255,220,140,.65)';
                    ctx.font = '600 8px sans-serif';
                    ctx.textAlign = 'center';
                    ctx.fillText('circle to seal', wp.x, wp.y - ringR - 5);
                }
            }
        }

        // ── the athame ───────────────────────────────────────────────────────────────────────
        const bp = toPx(blade);
        const ip = toPx(intent);
        if (down && Math.hypot(bp.x - ip.x, bp.y - ip.y) > 1.5) {
            // Show the guidance: a faint tether from where you pointed to where the blade went.
            ctx.strokeStyle = 'rgba(255,255,255,.16)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(ip.x, ip.y);
            ctx.lineTo(bp.x, bp.y);
            ctx.stroke();
        }
        ctx.fillStyle = down ? (offNow ? '#ff8f8f' : '#ffe9a8') : 'rgba(220,225,245,.55)';
        ctx.beginPath();
        ctx.arc(bp.x, bp.y, down ? 4.5 : 3, 0, Math.PI * 2);
        ctx.fill();
        if (down && offNow) {
            ctx.strokeStyle = 'rgba(255,90,90,.8)';
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.arc(bp.x, bp.y, 9, 0, Math.PI * 2);
            ctx.stroke();
        }

        // ── tension gauge ────────────────────────────────────────────────────────────────────
        const gx = cssW - PAD - gaugeW;
        const gTop = PAD + 6;
        const gBot = cssH - PAD - 18;
        const bandY = (v: number) => gBot - (v / 100) * (gBot - gTop);
        ctx.fillStyle = '#1a1a20';
        ctx.fillRect(gx - 2, gTop - 4, gaugeW + 4, (gBot - gTop) + 8);
        ctx.fillStyle = '#1f4c2e';
        ctx.fillRect(gx, bandY(TENSION_AMBER), gaugeW, gBot - bandY(TENSION_AMBER));
        ctx.fillStyle = '#7a5f22';
        ctx.fillRect(gx, bandY(TENSION_RED), gaugeW, bandY(TENSION_AMBER) - bandY(TENSION_RED));
        const hot = strainT > 0 && Math.floor(ts / 90) % 2 === 0;
        ctx.fillStyle = hot ? '#e5484d' : '#6d2226';
        ctx.fillRect(gx, gTop - 4, gaugeW, bandY(TENSION_RED) - gTop + 4);
        if (sim.floor > 1) {
            // The resting line: how much of the needle will NOT come back down.
            ctx.strokeStyle = 'rgba(255,170,90,.85)';
            ctx.lineWidth = 1.5;
            ctx.setLineDash([3, 3]);
            ctx.beginPath();
            ctx.moveTo(gx, bandY(sim.floor));
            ctx.lineTo(gx + gaugeW, bandY(sim.floor));
            ctx.stroke();
            ctx.setLineDash([]);
        }
        ctx.fillStyle = '#eef1f6';
        ctx.fillRect(gx - 3, bandY(Math.min(100, shownTension)) - 1.5, gaugeW + 6, 3);
        ctx.fillStyle = '#9aa2b2';
        ctx.font = '600 8px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(down ? 'TIGHTENING' : 'AT REST', gx + gaugeW / 2, gBot + 12);

        const capN = strokeDone.filter((x) => x).length;
        ctx.font = '600 10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#d2d8e4';
        ctx.fillText('rune ' + (runeIdx + 1) + ' / ' + geo.runes.length
            + '  ·  ' + capN + ' / ' + r.strokes.length + ' strokes', oxs + side / 2, cssH - 6);
    };

    requestAnimationFrame(frame);
}
